import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import { isValidPhoneNumber } from '@payaca/helpers/phoneNumberHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { TIconName } from '@payaca/untitled-icons';
import { FunctionComponent, useMemo, type JSX } from 'react';
import { useSelector } from 'react-redux';

type Props = {
  styleVariant?: InputStyleVariant;
  name: string;
  value?: any;
  label?: string;
  description?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconBefore?: TIconName;
  onChange?: (value: { [key: string]: string }) => void;
  onTouch?: (fieldName: string) => void;
  disableValidation?: boolean;
};

/**
 * A plain telephone input backed by the shared libphonenumber helper. It keeps
 * the field as free text (no masking) and, when the account region is known,
 * shows a non-blocking advisory if the number does not parse as valid — the
 * same UX the former Loqate check gave, without a server round-trip and without
 * the country-code masking that corrupted non-UK numbers.
 */
const PhoneInputField: FunctionComponent<Props> = ({
  styleVariant,
  name,
  value,
  label,
  description,
  isDisabled = false,
  isRequired = false,
  iconBefore,
  onChange,
  onTouch,
  disableValidation = false,
}: Props): JSX.Element => {
  const region: AccountRegions | undefined = useSelector(
    (state: any) => state?.users?.myProfile?.accounts?.[0]?.region
  );

  // Only advise when we can actually judge validity: the field is filled, the
  // account region is known (so a domestic number can be parsed) and advisory
  // validation has not been suppressed by the caller.
  const canAdvise = !disableValidation && !!value && !!region;

  const isValid = useMemo(
    () => (canAdvise ? isValidPhoneNumber(value, region) : undefined),
    [canAdvise, value, region]
  );

  const iconAfter: TIconName | undefined = useMemo(() => {
    if (isValid === undefined) return undefined;
    return isValid ? 'check' : 'x.3';
  }, [isValid]);

  const validationResult = useMemo(
    () =>
      isValid === false
        ? {
            isValid: false,
            errors: [
              'This phone number looks invalid to us, please double check before continuing',
            ],
          }
        : undefined,
    [isValid]
  );

  return (
    <div className="phone-input-field">
      <ValidatedFieldWrapper
        isTouched={value && value !== ''}
        validationResult={validationResult}
      >
        <BasicField
          styleVariant={styleVariant}
          name={name}
          value={value}
          label={label}
          description={description}
          isDisabled={isDisabled}
          isRequired={isRequired}
          iconBefore={iconBefore || 'phone.3'}
          onChange={onChange}
          onTouch={onTouch}
          iconAfter={iconAfter}
          type="tel"
        />
      </ValidatedFieldWrapper>
    </div>
  );
};

export default PhoneInputField;
